// src/utils/supportTeamReport.js
//
// The daily management workbook — "Project & Development Status", "Client
// Support & Complaints", "Running Project" and "Team Wise Pending" on one
// sheet.
//
// Extracted so more than one screen can produce it. It was reachable from the
// Overall Report alone, and the three calls it needs plus the shape-juggling
// around them are not something to copy into a second page: the moment the two
// copies disagree, two people comparing "the same" workbook are looking at
// different numbers.

import { format } from "date-fns";
import { ExportSupportTeamExcel } from "../components/SupportTeamExcelExport";
import { reportListsApis, excelClientWiseReportCountApis } from "../redux/report/reportActions";
import { teamWiseApis } from "../redux/team/teamActions";

/* Every one of these endpoints wraps its rows a different number of levels
   deep, and which one you get has changed before now. Unwrap defensively in
   one place rather than at each call site. */
const extractRows = (res) => {
  if (!res) return [];
  if (Array.isArray(res)) return res;
  if (Array.isArray(res.data)) return res.data;
  if (Array.isArray(res.data?.data)) return res.data.data;
  if (Array.isArray(res.data?.data?.data)) return res.data.data.data;
  if (Array.isArray(res.payload)) return res.payload;
  if (Array.isArray(res.payload?.data)) return res.payload.data;
  if (Array.isArray(res.payload?.data?.data)) return res.payload.data.data;
  return [];
};

/* Team Wise Pending needs its own unwrapper.
 *
 * extractRows above hunts for an ARRAY at each nesting level, and
 * /dashboard/team-wise does not answer with one — the controller splits the
 * procedure's output into { teams, grandTotal }. So every check fell through,
 * extractRows returned [], and the export skips its fourth table when the
 * array is empty. The Team Wise Pending block has therefore never once
 * appeared in the workbook: built, wired, fetched, and silently dropped one
 * step before it was drawn.
 *
 * The GRAND TOTAL row is put back on the end. The procedure emits it, the
 * controller lifts it out for the dashboard's benefit, and the export looks
 * for a row named "GRAND TOTAL" to style as the total — so it has to be in
 * the list again by the time the sheet is built.
 *
 * Still tolerates a bare array, in case the endpoint is ever flattened. */
const extractTeamWise = (res) => {
  const body = res?.data ?? res;
  const inner = body?.data ?? body;

  if (Array.isArray(inner)) return inner;

  const teams = Array.isArray(inner?.teams) ? inner.teams : [];
  return inner?.grandTotal ? [...teams, inner.grandTotal] : teams;
};

const toApiDate = (d) => (d ? format(d, "yyyy-MM-dd") : null);

/**
 * Fetch what the workbook needs and download it.
 *
 * @param dispatch   the store's dispatch
 * @param dateRange  { startDate, endDate } — the window for the detail and
 *                   client-wise tables
 * @param fallbackClientCounts  rows already in the store, used only if the
 *                   count call comes back empty
 * @returns {Promise<{ ok: boolean, reason?: string }>}
 */
export const downloadSupportTeamReport = async ({
  dispatch,
  dateRange,
  fallbackClientCounts = [],
}) => {
  const payload = {
    FromDate: toApiDate(dateRange?.startDate),
    ToDate: toApiDate(dateRange?.endDate),
  };

  const detail = extractRows(await dispatch(reportListsApis(payload)).unwrap());

  if (!detail.length) {
    return { ok: false, reason: "No data found for the selected date range." };
  }

  let clientCounts = extractRows(
    await dispatch(excelClientWiseReportCountApis(payload)).unwrap(),
  );
  if (!clientCounts.length) clientCounts = extractRows(fallbackClientCounts);

  /* Team Wise Pending goes out WITHOUT the date range, on purpose. The other
     tables answer "what happened in this window"; a backlog is not a window —
     a task raised three months ago and still Not Started is exactly the one
     worth seeing, and passing the range would hide it. The procedure treats
     both dates NULL as all-time for that reason.

     Non-fatal: three tables are still worth having if this one call fails. */
  let teamWise = [];
  try {
    teamWise = extractTeamWise(await dispatch(teamWiseApis({})).unwrap());
  } catch {
    teamWise = [];
  }

  ExportSupportTeamExcel(detail, dateRange, clientCounts, teamWise);
  return { ok: true };
};
